// frontend/src/operator/OperatorAuditLogs.js
// 運営パネル: 全施設の監査ログを閲覧
// - 組織フィルタ / 期間 / actor / action / target フィルタ + CSV エクスポート

import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { supabase } from '../supabase';

const OPERATOR_PATH = process.env.REACT_APP_OPERATOR_PATH || 'ops-7k3m9x2p4r8q';

const ACTION_LABELS = {
  login: 'ログイン', login_failed: 'ログイン失敗', logout: 'ログアウト',
  create: '作成', update: '更新', delete: '削除', read: '閲覧',
  access_denied: 'アクセス拒否',
  edit_session_start: '編集ON', edit_session_end: '編集OFF',
  other: 'その他',
};
const ACTOR_LABELS = {
  organization_staff: '施設スタッフ', organization_admin: '施設管理者',
  internal_staff: '自社スタッフ', operator: '運営', anonymous: '未認証',
};

const PERIOD_OPTIONS = [
  { value: '1d', label: '過去1日', days: 1 },
  { value: '7d', label: '過去7日', days: 7 },
  { value: '30d', label: '過去30日', days: 30 },
  { value: '90d', label: '過去90日', days: 90 },
  { value: '1y', label: '過去1年', days: 365 },
  { value: 'all', label: '全期間', days: null },
];

export default function OperatorAuditLogs() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('7d');
  const [filterOrgId, setFilterOrgId] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [filterActor, setFilterActor] = useState('all');
  const [search, setSearch] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const opt = PERIOD_OPTIONS.find((p) => p.value === period) || PERIOD_OPTIONS[2];
      const since = opt.days ? dayjs().subtract(opt.days, 'day').toISOString() : '2000-01-01T00:00:00Z';
      let q = supabase
        .from('audit_logs')
        .select('id, organization_id, actor_user_id, actor_email, actor_type, actor_role, action_type, target_table, target_id, target_summary, changes, ip_address, user_agent, success, error_message, created_at')
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(5000);
      if (filterOrgId !== 'all') q = q.eq('organization_id', filterOrgId);
      const { data, error: e } = await q;
      if (e) throw e;
      setLogs(data || []);
    } catch (e) {
      setError(`取得エラー: ${e.message}`);
    } finally { setLoading(false); }
  }, [period, filterOrgId]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('organizations').select('id, name').order('name');
      setOrgs(data || []);
    })();
  }, []);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const orgNameMap = useMemo(() =>
    Object.fromEntries((orgs || []).map((o) => [o.id, o.name])), [orgs]);

  const filtered = useMemo(() => {
    const kw = search.trim().toLowerCase();
    return logs.filter((l) => {
      if (filterAction !== 'all' && l.action_type !== filterAction) return false;
      if (filterActor !== 'all' && l.actor_type !== filterActor) return false;
      if (kw) {
        const hay = [l.actor_email, l.target_table, l.target_id, l.target_summary, l.organization_id]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(kw)) return false;
      }
      return true;
    });
  }, [logs, filterAction, filterActor, search]);

  const exportCsv = () => {
    const headers = ['created_at', 'organization_id', 'actor_email', 'actor_type', 'actor_role', 'action_type', 'target_table', 'target_id', 'target_summary', 'ip_address', 'user_agent', 'success', 'error_message'];
    const escape = (v) => {
      if (v == null) return '';
      const s = String(v);
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const rows = filtered.map((l) => headers.map((h) => escape(l[h])).join(','));
    const csv = '﻿' + headers.join(',') + '\n' + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `all_audit_logs_${dayjs().format('YYYYMMDD_HHmm')}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={() => navigate(`/${OPERATOR_PATH}`)} style={styles.backBtn}>← ダッシュボード</button>
          <h1 style={styles.title}>🔍 監査ログ (全施設)</h1>
        </div>
      </header>

      <main style={styles.main}>
        {error && <div style={styles.errorBox}>{error}</div>}

        <div style={styles.filterBar}>
          <label style={styles.lbl}>
            期間:{' '}
            <select value={period} onChange={(e) => setPeriod(e.target.value)} style={styles.sel}>
              {PERIOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label style={styles.lbl}>
            組織:{' '}
            <select value={filterOrgId} onChange={(e) => setFilterOrgId(e.target.value)} style={styles.sel}>
              <option value="all">全組織</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>
          <label style={styles.lbl}>
            操作:{' '}
            <select value={filterAction} onChange={(e) => setFilterAction(e.target.value)} style={styles.sel}>
              <option value="all">全て</option>
              {Object.entries(ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label style={styles.lbl}>
            実行者:{' '}
            <select value={filterActor} onChange={(e) => setFilterActor(e.target.value)} style={styles.sel}>
              <option value="all">全て</option>
              {Object.entries(ACTOR_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="🔍 メアド/テーブル/組織ID"
            style={{ ...styles.sel, minWidth: 220 }} />
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button onClick={fetchLogs} style={styles.reloadBtn}>↻ 再読み込み</button>
            <button onClick={exportCsv} disabled={filtered.length === 0} style={{
              ...styles.exportBtn,
              opacity: filtered.length === 0 ? 0.4 : 1,
              cursor: filtered.length === 0 ? 'not-allowed' : 'pointer',
            }}>
              📥 CSV ({filtered.length})
            </button>
          </div>
        </div>

        <div style={styles.metaLine}>
          取得件数: {logs.length} / 表示中: {filtered.length}
        </div>

        {loading ? (
          <div style={styles.message}>読み込み中...</div>
        ) : filtered.length === 0 ? (
          <div style={styles.message}>該当ログがありません</div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tr}>
                  <th style={styles.th}>日時</th>
                  <th style={styles.th}>組織</th>
                  <th style={styles.th}>実行者</th>
                  <th style={styles.th}>種別</th>
                  <th style={styles.th}>操作</th>
                  <th style={styles.th}>対象</th>
                  <th style={{ ...styles.th, textAlign: 'left' }}>要約</th>
                  <th style={styles.th}>IP</th>
                  <th style={styles.th}>結果</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} style={{ ...styles.tr, background: l.success === false ? 'rgba(220,38,38,0.12)' : undefined }}>
                    <td style={styles.td}>{dayjs(l.created_at).format('MM/DD HH:mm:ss')}</td>
                    <td style={{ ...styles.td, fontSize: 11 }}>{orgNameMap[l.organization_id] || l.organization_id || '-'}</td>
                    <td style={{ ...styles.td, textAlign: 'left', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {l.actor_email || '(anon)'}
                      {l.actor_role && <span style={{ color: '#64748b', marginLeft: 4 }}>[{l.actor_role}]</span>}
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        padding: '2px 6px', borderRadius: 8, fontSize: 10,
                        background: actorBg(l.actor_type), color: 'white', fontWeight: 700,
                      }}>{ACTOR_LABELS[l.actor_type] || l.actor_type}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        padding: '2px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700,
                        background: actionBg(l.action_type), color: 'white',
                      }}>{ACTION_LABELS[l.action_type] || l.action_type}</span>
                    </td>
                    <td style={{ ...styles.td, fontSize: 11, color: '#cbd5e1' }}>
                      {l.target_table || '-'}
                      {l.target_id && <div style={{ fontSize: 9, color: '#64748b' }}>{l.target_id}</div>}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'left', fontSize: 11 }}>
                      {l.target_summary || '-'}
                      {l.error_message && <div style={{ fontSize: 10, color: '#fca5a5', marginTop: 2 }}>{l.error_message}</div>}
                    </td>
                    <td style={{ ...styles.td, fontFamily: 'Menlo, monospace', fontSize: 10, color: '#94a3b8' }}>
                      {l.ip_address || '-'}
                    </td>
                    <td style={styles.td}>
                      {l.success === false
                        ? <span style={{ color: '#f87171', fontWeight: 700 }}>✕</span>
                        : <span style={{ color: '#4ade80', fontWeight: 700 }}>✓</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ fontSize: 10, color: '#64748b', marginTop: 8 }}>
          監査ログは改ざん防止のため追記のみ。 UPDATE / DELETE は DB レベルで拒否。 3省2ガイドライン準拠を目指した設計です。
        </div>
      </main>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#0f172a', color: '#e2e8f0' },
  header: { background: '#1e293b', padding: '16px 24px', borderBottom: '1px solid #334155' },
  title: { margin: 0, fontSize: 20, fontWeight: 700 },
  backBtn: { padding: '6px 14px', background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  main: { maxWidth: 1400, margin: '0 auto', padding: 20 },
  filterBar: { background: '#1e293b', padding: 12, borderRadius: 10, border: '1px solid #334155', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 },
  lbl: { fontSize: 12, color: '#94a3b8' },
  sel: { padding: '4px 8px', borderRadius: 6, border: '1px solid #475569', background: '#0f172a', color: '#e2e8f0', fontSize: 12 },
  reloadBtn: { padding: '6px 14px', background: '#334155', color: '#cbd5e1', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  exportBtn: { padding: '6px 14px', background: '#1d4ed8', color: 'white', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12 },
  metaLine: { fontSize: 11, color: '#64748b', marginBottom: 8 },
  errorBox: { background: 'rgba(239,68,68,0.15)', color: '#fca5a5', padding: '10px 14px', borderRadius: 8, fontSize: 12, marginBottom: 12 },
  tableWrap: { background: '#1e293b', borderRadius: 10, border: '1px solid #334155', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  tr: { borderBottom: '1px solid #334155' },
  th: { padding: 8, textAlign: 'center', fontWeight: 700, color: '#94a3b8', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px' },
  td: { padding: '6px 8px', textAlign: 'center' },
  message: { padding: 40, textAlign: 'center', color: '#94a3b8' },
};

const actorBg = (t) => ({
  organization_staff: '#3b82f6', organization_admin: '#7c3aed',
  internal_staff: '#0891b2', operator: '#dc2626', anonymous: '#64748b',
})[t] || '#64748b';
const actionBg = (t) => ({
  login: '#1d4ed8', logout: '#475569', login_failed: '#991b1b', access_denied: '#991b1b',
  create: '#15803d', update: '#a16207', delete: '#991b1b', read: '#4338ca',
  edit_session_start: '#a16207', edit_session_end: '#a16207',
})[t] || '#475569';
